import { z } from 'zod';

// ─── Event Envelope ─────────────────────────────────────────────────
// Standard metadata wrapper for every event flowing through the system.
// Mirrors the runtime EventEnvelope in @nexus/backend-core but defined
// here as a Zod contract so publishers and consumers share one schema.

export const EventEnvelopeSchema = z.object({
    /** Unique message ID */
    id: z.string().uuid(),
    /** Amber Path correlation tracking */
    correlationId: z.string().uuid(),
    /** Dedup key for idempotent consumers */
    idempotencyKey: z.string().optional(),
    /** Domain topic the message was published to (e.g. NEXUS.USERS) */
    topic: z.string().min(1),
    /** Original event name used for client-side routing (e.g. USER.REGISTERED) */
    eventType: z.string().min(1).optional(),
    /** The actual message data — narrowed per event */
    payload: z.unknown(),
    /** Key-value metadata (tenantId, userId, actionType, etc.) */
    attributes: z.record(z.string(), z.string()),
    /** ISO-8601 publish timestamp */
    publishedAt: z.string().datetime(),
});

export type EventEnvelope<T = unknown> = Omit<z.infer<typeof EventEnvelopeSchema>, 'payload'> & {
    payload: T;
};

/**
 * Creates a typed envelope schema for a specific payload.
 * Usage: `const schema = typedEnvelopeSchema(MyPayloadSchema);`
 */
export function typedEnvelopeSchema<T extends z.ZodTypeAny>(payloadSchema: T) {
    return EventEnvelopeSchema.extend({ payload: payloadSchema });
}
